import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { pick, without } from 'lodash-es'
import { Input, Button } from '@ow/comps'
import { useUserList } from '@vt/apis/lib-ex/v2/users'
import { useAppEx } from '@vt/hooks'
import { Table, Checkbox, Pagination, LoadingOverlay } from '@mantine/core'
import Filter from './filter'
import UserTags from './userTags'

const PAGESIZE = 50

const breakWord = (data, max) => {
  if (data && data.length > max) {
    return `${data.slice(0, max)}...`
  }
  return data
}

const RULETYPE = {
  EQUAL: 'equal',
  QUERY: 'query',
  INCLUDE: 'include',
}

const filt = (data, rule) => {
  if (!rule) return true
  const { key, value, type } = rule
  switch (type) {
    case RULETYPE.EQUAL:
      return data[key] === value
    case RULETYPE.QUERY:
      return data[key] && data[key].includes(value)
    case RULETYPE.INCLUDE:
      return value.includes(data[key])
    default:
      return true
  }
}

const ThWithFilter = ({ label, children }) => {
  return (
    <th>
      <div className="inline-flex items-center gap-2">
        <span>{label}</span>
        {children}
      </div>
    </th>
  )
}

const UserList = function ({
  orgId,
  userIds,
  rightBtn,
  value,
  onChange,
  onUserFilter,
}) {
  const isSelectMode = !userIds
  const { appId } = useAppEx((x) => pick(x, ['appId']))

  const { orgUsers, isLoading } = useUserList(appId, orgId)

  const [search, setSearch] = useState('')

  const [userData, setUserData] = useState([])
  const [total, setTotal] = useState(0)

  const [pageNo, setPageNo] = useState(1)

  const [orgOptions, setOrgOptions] = useState([])
  const [roleOptions, setRoleOptions] = useState([])

  const allListRef = useRef([])
  const filterListRef = useRef([])

  const rulesRef = useRef([])

  const setSelectedUserList = (userId, checked) => {
    if (checked) {
      onChange([...value, userId])
    } else {
      onChange(without(value, userId))
    }
  }

  const setPageData = useCallback((page) => {
    setPageNo(page)
    const startIndex = (page - 1) * PAGESIZE
    setUserData(filterListRef.current.slice(startIndex, startIndex + PAGESIZE))
  }, [])

  const setOptions = useCallback(() => {
    const orgSet = new Set()
    const roleSet = new Set()
    allListRef.current.forEach((item) => {
      if (item.org) orgSet.add(item.org)
      if (item.role) roleSet.add(item.role)
    })

    setOrgOptions(Array.from(orgSet))
    setRoleOptions(Array.from(roleSet))
  }, [])

  const onFilter = useCallback(() => {
    if (!allListRef.current.length || !rulesRef.current.length) {
      filterListRef.current = allListRef.current
    } else {
      filterListRef.current = allListRef.current.filter((user) => {
        for (let i = 0; i < rulesRef.current.length; i++) {
          if (!filt(user, rulesRef.current[i])) {
            return false
          }
        }
        return true
      })
    }

    setPageData(1)
    setTotal(Math.ceil(filterListRef.current.length / PAGESIZE))
    if (onUserFilter) {
      onUserFilter(filterListRef.current)
    }
  }, [])

  const onSearch = useCallback(() => {
    rulesRef.current[0] = {
      key: 'name',
      value: search,
      type: RULETYPE.QUERY,
    }
    onFilter()
  }, [search])

  const onTableHeaderFilter = useCallback((v, key) => {
    const index = {
      role: 1,
      org: 2,
    }[key]
    if (!v.length) {
      rulesRef.current[index] = null
    } else {
      rulesRef.current[index] = {
        key,
        value: v,
        type: RULETYPE.INCLUDE,
      }
    }

    onFilter()
  }, [])

  useEffect(() => {
    if (!orgUsers) return
    if (userIds) {
      allListRef.current = orgUsers.filter((user) => userIds.includes(user.id))
    } else {
      allListRef.current = orgUsers
    }
    setOptions()
    onFilter()
  }, [orgUsers, userIds])

  return (
    <div className="flex flex-col gap-4 flex-1 overflow-hidden">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div>
            <Input
              className="w-[300px]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button type="primary" onClick={onSearch}>
            查询
          </Button>
        </div>
        {rightBtn}
      </div>
      <div
        className={clsx(
          'w-full overflow-y-auto',
          isSelectMode ? 'h-72' : 'flex-1'
        )}
      >
        <LoadingOverlay visible={isLoading} />
        <Table>
          <thead>
            <tr className="bg-primary-50">
              {isSelectMode && (
                <th>
                  <span />
                </th>
              )}
              <th>用户姓名</th>
              <th>用户名</th>
              <th>手机号</th>
              <ThWithFilter label="组织">
                <Filter
                  name="org"
                  options={orgOptions}
                  onChange={onTableHeaderFilter}
                />
              </ThWithFilter>
              <ThWithFilter label="角色">
                <Filter
                  name="role"
                  options={roleOptions}
                  onChange={onTableHeaderFilter}
                />
              </ThWithFilter>
            </tr>
          </thead>
          <tbody>
            {userData.map((user) => (
              <tr key={user.id}>
                {isSelectMode && (
                  <td>
                    <Checkbox
                      checked={value.includes(user.id)}
                      onChange={(v) =>
                        setSelectedUserList(user.id, v.target.checked)
                      }
                    />
                  </td>
                )}
                <td>
                  {user.name && (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full mr-1  flex items-center justify-center bg-primary-700">
                        <span className="text-white opacity-80">
                          {user.name[user.name.length - 1]}
                        </span>
                      </div>
                      {breakWord(user.name, 15)}
                    </div>
                  )}
                </td>
                <td>{breakWord(user.username, 10)}</td>
                <td>{breakWord(user.mobile, 11)}</td>
                <td>{user.org}</td>
                <td>{user.role}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div className="flex items-center justify-between gap-4">
        {isSelectMode ? (
          <UserTags value={value} onChange={onChange} userData={orgUsers} />
        ) : (
          <div className="text-sm">共查询到 {userIds.length} 条数据</div>
        )}
        {total > 0 && (
          <Pagination
            page={pageNo}
            total={total}
            className="flex-nowrap flex-shrink-0"
            onChange={setPageData}
          />
        )}
      </div>
    </div>
  )
}

export default memo(UserList)
